import { zodResolver } from '@hookform/resolvers/zod';
import {
  ApiError,
  type CatalogueItemResource,
  type CataloguePriceResource,
  CataloguePricesController_reprice_v1,
  type CataloguePricesController_reprice_v1Body,
} from '@repo/api-client';
import { Alert, AlertDescription } from '@repo/ui/components/alert';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useApiClient } from '@/api/api-client';
import { messageForFailure } from '@/api/failure';
import { priceInRupeesSchema } from '@/lib/field-schemas';
import { IDEMPOTENCY_KEY_HEADER, newIdempotencyKey } from '@/lib/idempotency-key';
import { formatPaise, paiseFromRupees, rupeesFromPaise } from '@/lib/money';

import { fieldLabel, fieldSurface, FormDialogContent } from '../auth/AuthFrame';
import { formatInstant } from './instant';
import { formatTaxRate, taxFields, taxOnTheWire, taxValuesFrom, withTaxRule } from './tax';
import { TaxFields } from './TaxFields';

/**
 * Setting the next price: `POST /v1/catalogue/items/{id}/prices`.
 *
 * ## Both amounts, every time, including the one that did not move
 *
 * `RepriceDto` requires `sellingPaise` **and** `costPaise`, and the document says
 * why: a price row is "a complete statement rather than a delta (R-202)". A row
 * states what the item cost and what it sold for from that instant, and a sale
 * later references the row's id — so a row that only carried the field somebody
 * changed would leave the other one answerable by nothing except walking
 * backwards through the history.
 *
 * That is a form problem as much as a contract one: somebody moving the selling
 * price alone did not intend to restate the cost, and if the form arrives blank
 * they will type something, guess, or leave it at zero. **So both fields open
 * prefilled from the price in force**, and the copy says the row restates both.
 * The cost that "did not change" is genuinely being written again, and that is
 * worth showing rather than hiding behind an optional field.
 *
 * ## There is nothing here to backdate with, deliberately
 *
 * `effectiveFrom` and `setByActorId` come from the server — its clock and its
 * authenticated principal. Sending either is refused as an unknown field
 * (`400 VALIDATION_UNKNOWN_FIELD`) rather than ignored, because a caller who can
 * name the instant can backdate what the park charged and a caller who can name
 * the actor can attribute it to somebody else. There is no field for either
 * below, and the body is typed as the generated `…_v1Body`, so adding one would
 * not compile.
 *
 * ## No client-side price rules
 *
 * Zero is valid — a free child ticket is `₹0.00` — and selling below cost is
 * valid, because loss leaders exist. Neither earns a warning here. The only
 * thing refused at the field is an amount this client cannot carry without
 * rounding it (`isRepresentablePaise`), which is not a business rule: past
 * `MAX_SAFE_INTEGER` the value stops being the value somebody typed before the
 * request is even built. Everything else is the API's to refuse, as
 * `CATALOGUE_PRICE_INVALID`.
 *
 * ## The GST pair is always sent, prefilled like the amounts
 *
 * `RepriceDto` reads a pair left out as "keep the tax of the price being
 * replaced", and two nulls as "not taxed". This form never leaves it out: it
 * opens on the pair in force (`taxValuesFrom`) and sends what it shows, so a
 * row states its tax as completely as its amounts and the switch means what it
 * says. A reprice that only moves the price restates the same pair.
 */

const repriceSchema = withTaxRule(
  z.object({
    sellingRupees: priceInRupeesSchema,
    costRupees: priceInRupeesSchema,
    ...taxFields,
  }),
);

type RepriceValues = z.infer<typeof repriceSchema>;

export function RepriceDialog({
  open,
  onOpenChange,
  item,
  current,
  onRepriced,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: CatalogueItemResource;

  /** The price in force, which both fields open at. `null` if none was read. */
  current: CataloguePriceResource | null;

  onRepriced: (price: CataloguePriceResource) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <FormDialogContent>
        <DialogHeader>
          <DialogTitle>Reprice {item.name}</DialogTitle>
          <DialogDescription>
            This writes a new entry stating both amounts from now on. The price it replaces stays in
            the history and cannot be edited or removed — if this is a correction, both entries will
            be visible.
          </DialogDescription>
        </DialogHeader>

        {/* The form is a child so that closing the dialog unmounts it — Base UI's
            portal does not keep its content mounted — which is what makes the
            next open start from the price in force rather than from a
            half-edited attempt, and what makes the idempotency key below belong
            to one attempt at one reprice.
            `key` covers the case the unmount does not: after a successful write
            the screen re-reads and `current` becomes the row that was just
            written, and a form that survived that would open the *next* reprice
            prefilled with what used to be in force. */}
        <RepriceForm
          key={current?.id ?? 'none'}
          item={item}
          current={current}
          onRepriced={onRepriced}
        />
      </FormDialogContent>
    </Dialog>
  );
}

function RepriceForm({
  item,
  current,
  onRepriced,
}: {
  item: CatalogueItemResource;
  current: CataloguePriceResource | null;
  onRepriced: (price: CataloguePriceResource) => void;
}) {
  const client = useApiClient();

  const form = useForm({
    resolver: zodResolver(repriceSchema),
    mode: 'onTouched',
    defaultValues: {
      // `rupeesFromPaise` and not `formatPaise`: this value goes back into a
      // field and has to be something a person could have typed. `formatPaise`
      // adds `₹` and Indian grouping, both of which `RUPEES_SHAPE` refuses, so
      // it would open a form that is invalid before anybody touches it.
      sellingRupees: current === null ? '' : rupeesFromPaise(current.sellingPaise),
      costRupees: current === null ? '' : rupeesFromPaise(current.costPaise),
      ...taxValuesFrom(current),
    },
  });

  const [formError, setFormError] = useState<string | null>(null);

  // One key for as long as this form is mounted, which is exactly one attempt at
  // one reprice: the dialog unmounts on success, so the next reprice gets a
  // fresh one. A retry after a dropped answer carries the same key and the same
  // payload, so the API replays its first answer rather than writing a second
  // row — which on an append-only table is the failure worth preventing, because
  // a duplicate row cannot be deleted afterwards.
  const [idempotencyKey] = useState(newIdempotencyKey);

  const pending = form.formState.isSubmitting;

  const handleSubmit = async (values: RepriceValues) => {
    // Belt and braces with the disabled button below. A second submit carries
    // the same key and payload, so the API replays rather than writing twice —
    // but it would still put this form into a second in-flight state for nothing.
    if (pending) return;

    setFormError(null);

    try {
      // The body's shape comes from the generated client; nothing about this
      // route is written down here. Both amounts are converted from the rupees
      // somebody typed into the integer paise the API carries — `2500`, never
      // `25.00` and never `'25.00'`. Nothing else rides along — no instant, no
      // actor — and the type is what says so.
      const body: CataloguePricesController_reprice_v1Body = {
        sellingPaise: paiseFromRupees(values.sellingRupees),
        costPaise: paiseFromRupees(values.costRupees),
        ...taxOnTheWire(values),
      };

      onRepriced(
        await client.request(CataloguePricesController_reprice_v1, {
          path: { id: item.id },
          body,
          headers: { [IDEMPOTENCY_KEY_HEADER]: idempotencyKey },
        }),
      );
    } catch (error: unknown) {
      const field = fieldRefused(error);

      if (field !== null) {
        // A 422 that names one amount is set on that amount, per
        // docs/ui-conventions.md, and the focus goes with it — somebody who
        // submitted with the keyboard is otherwise left with the caret on the
        // button and a message they have to go and find.
        form.setError(field, { message: messageForFailure(error) });
        form.setFocus(field);
      } else {
        setFormError(messageForFailure(error));
      }

      // Nothing is cleared. The retry is one corrected amount, and re-typing the
      // other to fix it is how a cost gets restated by accident.
    }
  };

  return (
    // One column, per docs/ui-conventions.md. `noValidate` because the browser's
    // own bubbles are not this app's error language and cannot be worded.
    <Form {...form}>
      <form
        className="space-y-5"
        noValidate
        onSubmit={(event) => void form.handleSubmit(handleSubmit)(event)}
      >
        {current !== null && (
          // What is being replaced, stated where it can be compared against what
          // is being typed. `text-foreground` on `bg-muted` and not
          // `text-muted-foreground`, which measures 4.39:1 and misses AA — the
          // one contrast trap in this system, and this is exactly the quiet
          // inset panel that walks into it.
          <div className="space-y-1 rounded-2xl bg-muted px-4 py-3 text-sm text-foreground">
            <p>
              In force now:{' '}
              <span className="font-mono numeric">{formatPaise(current.sellingPaise)}</span>{' '}
              selling, <span className="font-mono numeric">{formatPaise(current.costPaise)}</span>{' '}
              cost.
            </p>
            <p>
              {current.hsnCode === null || current.taxRateBasisPoints === null ? (
                'No GST.'
              ) : (
                <>
                  GST{' '}
                  <span className="font-mono numeric">
                    {formatTaxRate(current.taxRateBasisPoints)}
                  </span>
                  , HSN <span className="font-mono numeric">{current.hsnCode}</span>.
                </>
              )}
            </p>
            <p className="text-foreground/70">Set {formatInstant(current.effectiveFrom)}.</p>
          </div>
        )}

        <PriceField
          control={form.control}
          name="sellingRupees"
          label="Selling price"
          description="What a guest pays from now on. Zero is allowed, and so is selling below cost."
        />

        <PriceField
          control={form.control}
          name="costRupees"
          label="Cost price"
          description={
            current === null
              ? 'What the garden pays for it from now on.'
              : 'What the garden pays for it from now on. Prefilled with the cost in force — leave it as it is if only the selling price is moving, and it will be restated on the new entry.'
          }
        />

        <TaxFields />

        <p className="text-sm text-muted-foreground">
          The moment this takes effect is the server&rsquo;s, and so is the record of who set it.
          Neither can be chosen here, so a price cannot be backdated.
        </p>

        {/* Above the submit and it stays there — a toast vanishes, and somebody
            who looked away has no way to get it back. */}
        {formError !== null && (
          <Alert variant="destructive">
            <IconAlertTriangle />
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button type="submit" disabled={pending}>
            {pending ? 'Saving…' : 'Save new price'}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}

/**
 * Which field a refusal was about, or `null` for one that was about neither.
 *
 * `CATALOGUE_PRICE_INVALID` carries `details.field` naming which of the two
 * amounts, and **never the value** — the document is explicit that cost price is
 * owner-only and an error body is one of the places it must not leak. So the
 * name is all there is to read, and it is read defensively: `details` is
 * `Record<string, unknown>`, the API may name a field this form does not have,
 * and a refusal attached to no field is better than one attached to the wrong
 * one.
 */
function fieldRefused(error: unknown): 'sellingRupees' | 'costRupees' | null {
  if (!(error instanceof ApiError) || error.code !== 'CATALOGUE_PRICE_INVALID') return null;

  const field = error.details?.['field'];

  if (field === 'sellingPaise') return 'sellingRupees';
  if (field === 'costPaise') return 'costRupees';

  return null;
}

/**
 * A price field. Two of them, identical apart from their copy.
 *
 * Typed in rupees and sent in paise, and the `₹` is on the field rather than in
 * the value — a symbol somebody can type is a symbol the parser has to have an
 * opinion about. `inputMode="decimal"` gets the right keypad without
 * `type="number"`, which would hand the browser its own opinion about what a
 * price is: a spinner, a scroll wheel that changes the amount under a pointer,
 * and a value that reads back as `NaN` when it is empty.
 *
 * The same shape as the one in `NewCatalogueItemDialog`, and deliberately not
 * shared with it yet: `docs/ui-conventions.md` asks for a second consumer before
 * something is promoted, and these two are that second consumer arriving. The
 * rule they both hold — `priceInRupeesSchema` — already lives in one place,
 * which is the half that matters, because a divergence there would be two forms
 * disagreeing about what a price is rather than two forms looking slightly
 * different.
 */
function PriceField({
  control,
  name,
  label,
  description,
}: {
  control: ReturnType<typeof useForm<RepriceValues>>['control'];
  name: 'sellingRupees' | 'costRupees';
  label: string;
  description: string;
}) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel required className={fieldLabel}>
            {label}
          </FormLabel>
          <div className="relative">
            <span
              // Decorative: the label already says what this is, and a reader
              // hearing "rupees" between the label and the value gains nothing.
              aria-hidden
              className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-muted-foreground"
            >
              ₹
            </span>
            <FormControl
              render={
                <Input
                  className={`${fieldSurface} h-11 pl-8 font-mono numeric`}
                  required
                  inputMode="decimal"
                  placeholder="25.00"
                  autoComplete="off"
                  {...field}
                />
              }
            />
          </div>
          <FormDescription>{description}</FormDescription>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
